import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
} from "react";
import { excludeViewCountLabels, FAVORITE_LABEL } from "../../config";

const DashboardContext = createContext();

export const useDashboardContext = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error(
      "useDashboardContext must be used within a DashboardProvider",
    );
  }
  return context;
};

export const DashboardProvider = ({ children, containerRef = null }) => {
  // Signal states to notify child components of changes
  const [lastDeletedDashboard, setLastDeletedDashboard] = useState(null);
  const [lastReloadedDashboard, setLastReloadedDashboard] = useState(null);
  const [globalDeletedDashboard, setGlobalDeletedDashboard] = useState(null);
  const [lastUpdatedThumbnail, setLastUpdatedThumbnail] = useState(null);

  // Preview states (moved from DashboardList)
  const [selectedReport, setSelectedReport] = useState({});
  const [modalPositionX, setModalPositionX] = useState(0);
  const [modalPositionY, setModalPositionY] = useState(0);
  const [disableViewCount, setDisableViewCount] = useState(false);
  const [currentLabel, setCurrentLabel] = useState(undefined);
  const [modalTrigger, setModalTrigger] = useState(null);

  const [selectedThemeDashboard, setSelectedThemeDashboard] = useState(null);
  const [generateReportInfo, setGenerateReportInfo] = useState({
    id: null,
    visible: false,
    templateValid: false,
  });
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  // Centralized Caches
  const imageCache = useRef(new Map());
  const errorCache = useRef(new Set());

  // Shared scroll container ref (can be provided by parent or created internally)
  const internalScrollContainerRef = useRef(null);
  const scrollContainerRef = containerRef || internalScrollContainerRef;

  // Track active sliders to toggle container overflow when sliders are active
  const activeSliders = useRef(new Set());
  const onSliderLockChange = useCallback((id, active) => {
    if (!id) return;
    if (active) {
      activeSliders.current.add(id);
    } else {
      activeSliders.current.delete(id);
    }

    try {
      if (scrollContainerRef && scrollContainerRef.current) {
        scrollContainerRef.current.style.overflow =
          activeSliders.current.size > 0 ? "hidden" : "auto";
      }
    } catch (e) {
      // ignore DOM errors in server-side or test environments
    }
  }, [scrollContainerRef]);

  /**
   * Update modal positions on window resize
   */
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
      if (modalTrigger) {
        const rect = modalTrigger.getBoundingClientRect();
        setModalPositionX(rect.left);
        setModalPositionY(rect.top);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [modalTrigger]);

  /**
   * Signal dashboard reload
   */
  const dashboardReloaded = useCallback((dashboard) => {
    setLastReloadedDashboard(dashboard);
    setLastDeletedDashboard(null);
  }, []);

  /**
   * Signal dashboard delete (temporary)
   */
  const dashboardDelete = useCallback((dashboard) => {
    setLastDeletedDashboard(dashboard);
    setLastReloadedDashboard(null);
  }, []);

  /**
   * Signal global permanent delete
   */
  const dashboardDeletedPermanently = useCallback(
    (dashboard) => {
      dashboardDelete(dashboard); // Also trigger temporary delete signal
      setGlobalDeletedDashboard(dashboard);
    },
    [dashboardDelete],
  );

  const updateCustomThumbnail = useCallback(
    (id, thumbnail) => {
      setLastUpdatedThumbnail({ id, thumbnail });
      if (selectedReport && selectedReport.id === id) {
        setSelectedReport((prev) => ({ ...prev, customThumbnail: thumbnail }));
      }
    },
    [selectedReport],
  );

  /**
   * select report for preview and prepare to show preview
   */
  const selectReport = useCallback((data, x, y, label, trigger) => {
    const filteredLabels = [];

    for (let l of data.labels) {
      if (l.id === FAVORITE_LABEL || l.name === FAVORITE_LABEL) {
        continue;
      }

      filteredLabels.push(l);
    }

    let isDisabledViewCount = excludeViewCountLabels.has(label?.name);

    setSelectedReport({
      ...data,
      labels: filteredLabels
    });

    let finalX = x;
    let finalY = y;
    if (trigger && x === 0 && y === 0) {
      const rect = trigger.getBoundingClientRect();
      finalX = rect.left;
      finalY = rect.top;
    }

    setModalPositionX(finalX);
    setModalPositionY(finalY);
    setDisableViewCount(isDisabledViewCount);
    setCurrentLabel(label);
    setModalTrigger(trigger);
  }, []);

  /**
   * close dashboard preview
   */
  const previewClose = useCallback(() => {
    setSelectedReport({});
    document.querySelectorAll(".report").forEach((element) => {
      element.classList.remove("active");
    });
  }, []);

  /**
   * select dashboard for theme selection
   */
  const selectTheme = useCallback((data, x, y, label, trigger) => {
    setSelectedThemeDashboard(data);

    let finalX = x;
    let finalY = y;
    if (trigger && x === 0 && y === 0) {
      const rect = trigger.getBoundingClientRect();
      finalX = rect.left;
      finalY = rect.top;
    }

    setModalPositionX(finalX);
    setModalPositionY(finalY);
    setCurrentLabel(label);
    setModalTrigger(trigger);
  }, []);

  /**
   * close theme selection
   */
  const themeClose = useCallback(() => {
    setSelectedThemeDashboard(null);
  }, []);

  /**
   * open generate report modal
   */
  const openGenerateReport = useCallback((id, templateValid) => {
    setGenerateReportInfo({ id, visible: true, templateValid });
    window.dispatchEvent(new CustomEvent("close-top-menu-popups"));
  }, []);

  /**
   * close generate report modal
   */
  const closeGenerateReport = useCallback(() => {
    setGenerateReportInfo((prev) => ({ ...prev, visible: false }));
  }, []);

  const value = {
    // States
    lastDeletedDashboard,
    lastReloadedDashboard,
    globalDeletedDashboard,
    lastUpdatedThumbnail,
    selectedReport,
    modalPositionX,
    modalPositionY,
    disableViewCount,
    setDisableViewCount,
    currentLabel,
    modalTrigger,
    selectedThemeDashboard,
    generateReportInfo,
    windowSize,
    imageCache,
    errorCache,

    // Actions
    dashboardReloaded,
    dashboardDelete,
    dashboardDeletedPermanently,
    updateCustomThumbnail,
    selectReport,
    previewClose,
    selectTheme,
    themeClose,
    openGenerateReport,
    closeGenerateReport,
    // Expose shared container ref and slider lock API
    scrollContainerRef,
    onSliderLockChange,
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};
